import './FileDropArea.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { SonarText } from '@ui/text';

import { Accept, useDropzone } from 'react-dropzone';
import { Case, Default, Switch } from 'react-if';
import { CheckCircleOutlined, CloseCircleOutlined, InboxOutlined } from '@ant-design/icons';
import React, { memo, useEffect } from 'react';

const defaultMaxSize = 5242880;
const defaultImageAccept: Accept = {
	'image/*': []
};

export interface FileDropAreaProps {
	accept?: Accept;
	maxSize?: number;
	minSize?: number;
	multiple?: boolean;
	onAccept: (acceptedFiles: readonly File[]) => void;
	unsupportedMessage?: string;
}

export const FileDropArea: React.FC<FileDropAreaProps> = memo(
	({
		accept = defaultImageAccept,
		maxSize = defaultMaxSize,
		minSize = 0,
		multiple = false,
		onAccept,
		unsupportedMessage = COPY_SPECIFIC.UNSUPPORTED_FILE_TYPE_GENERIC
	}) => {
		const { acceptedFiles, isDragActive, isDragAccept, isDragReject, fileRejections, getRootProps, getInputProps } =
			useDropzone({
				accept,
				maxSize,
				minSize,
				multiple
			});

		useEffect(() => {
			if (acceptedFiles?.length) {
				onAccept(acceptedFiles);
			}
		}, [ acceptedFiles, onAccept ]);

		const isFileTooLarge = fileRejections.length > 0 && fileRejections[ 0 ].file.size > maxSize;

		let message = COPY_SPECIFIC.CLICK_OR_DRAG_FILE_TO_THIS_AREA_TO_UPLOAD;

		if (isDragReject) {
			message = unsupportedMessage;
		}

		if (isFileTooLarge) {
			message = COPY_SPECIFIC.FILE_IS_TOO_LARGE(maxSize);
		}

		const isInvalid = (isDragActive && isDragReject) || (!isDragActive && isFileTooLarge);
		const isValid = isDragActive && isDragAccept;

		const iconClassNames = classNames('upload-icon', {
			invalid: isInvalid,
			valid: isValid
		});

		return (
			<section { ...getRootProps({ className: 'file-drop-area' }) }>
				<input { ...getInputProps() } />
				<span className={ iconClassNames }>
					<Switch>
						<Case condition={ isInvalid }>
							<CloseCircleOutlined />
						</Case>
						<Case condition={ isValid }>
							<CheckCircleOutlined />
						</Case>
						<Default>
							<InboxOutlined />
						</Default>
					</Switch>
				</span>
				<SonarText size={ 16 }>{ message }</SonarText>
			</section>
		);
	}
);
